import { narrowType } from '../../types/assertType';
import { isDefined } from '../../types/isDefined';
import { pick } from '../../utils/pick';
import type { TableColumn, TableEndpoint } from '../TableEndpoint';

/**
 * Full new list of columns to be used by alternative table
 *
 * Use either strings to pick columns from old definition
 * or add in fully new column definitions.
 *
 * Use '...' to list all available columns.
 *
 * @see createAlternativeTableColumns
 * @see createAlternativeTableEndpoint
 */
export type AlternativeTableColumnList<TRow> = (
  | TableColumn<TRow>
  | string
  | '...'
)[];

/**
 * TableEndpoint fields to override
 *
 * @see TableEndpoint
 */
export interface AlternativeTableEndpointOptions<TRow> {
  /**
   * Intentionally mandatory, alternative table will likely have separate settings etc
   */
  id: string;
  webPath?: string;
  title?: string;
  description?: string;
  webDependencies?: string[];
  columns: AlternativeTableColumnList<TRow>;
}

export const createAlternativeTableColumns = <TRow>(
  endpoint: TableEndpoint<TRow>,
  columns: AlternativeTableColumnList<TRow>,
): TableColumn<TRow>[] => {
  const columnsByKey = endpoint.columns.reduce<
    Record<string, TableColumn<TRow>>
  >((acc, column) => {
    acc[column.key] = column;
    return acc;
  }, {});
  const touchedKeys = new Set();
  const orderedResult = columns.map((column) => {
    if (column === '...') {
      return '...';
    }
    if (typeof column === 'string') {
      touchedKeys.add(column);
      return narrowType(isDefined, columnsByKey[column]);
    }
    touchedKeys.add(column.key);
    return column;
  });

  return orderedResult.reduce<TableColumn<TRow>[]>((acc, current) => {
    if (current === '...') {
      acc.push(...endpoint.columns.filter((c) => !touchedKeys.has(c.key)));
    } else {
      acc.push(current);
    }
    return acc;
  }, []);
};

/**
 * Pick a table endpoint and update it for different non-default ApiTable usage
 *
 * For example pick only subset of columns, add new columns, update title,
 * add custom ID to use separate settings instance etc.
 *
 * @see AlternativeTableEndpointOptions
 */
export const createAlternativeTableEndpoint = <TRow>(
  endpoint: TableEndpoint<TRow>,
  options: AlternativeTableEndpointOptions<TRow>,
): TableEndpoint<TRow> => ({
  ...endpoint,
  ...pick(['id', 'webPath', 'title', 'description', 'webDependencies'])(
    options,
  ),
  columns: createAlternativeTableColumns(endpoint, options.columns),
});
